import React, { useState, useEffect } from 'react';
import { userAPI, groupsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Trash2, UserPlus, Users, Mail, X } from 'lucide-react';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState('');
  const [notificationError, setNotificationError] = useState(false);

  // Group state
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupInvitations, setGroupInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [groupLoading, setGroupLoading] = useState(true);

  const isAdmin = currentUser && currentUser.userlevel >= 200;
  const isStaff = currentUser && currentUser.userlevel >= 100;

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isStaff) {
      fetchGroup();
    } else {
      setGroupLoading(false);
    }
  }, [isStaff]);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAllUsers();
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 403) setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroup = async () => {
    if (!isStaff) return;
    try {
      const [membersRes, invitationsRes] = await Promise.all([
        groupsAPI.listMembers(),
        groupsAPI.listInvitations(),
      ]);
      if (membersRes.data.success) setGroupMembers(membersRes.data.members);
      if (invitationsRes.data.success) setGroupInvitations(invitationsRes.data.invitations);
    } catch (error) {
      console.error('Error fetching group:', error);
    } finally {
      setGroupLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviteLoading(true);
    try {
      const response = await groupsAPI.invite(email);
      if (response.data.success) {
        setInviteEmail('');
        showNotification('Invitation sent to ' + email);
        fetchGroup();
      } else {
        showNotification(response.data.error || 'Failed to send invitation', true);
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Failed to send invitation';
      showNotification(msg, true);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvitation = async (id) => {
    try {
      await groupsAPI.cancelInvitation(id);
      showNotification('Invitation cancelled');
      fetchGroup();
    } catch (error) {
      showNotification('Failed to cancel invitation', true);
    }
  };

  const handleUpdateUser = async (userId, field, value) => {
    try {
      const updateData = { [field]: value };
      const response = await userAPI.updateUser(userId, updateData);

      if (response.data.success) {
        setUsers(users.map(user =>
          user.id === userId ? { ...user, [field]: value } : user
        ));
        showNotification('User updated successfully');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      showNotification('Failed to update user', true);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      const response = await userAPI.deleteUser(userId);
      if (response.data.success) {
        setUsers(users.filter(user => user.id !== userId));
        showNotification('User deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Failed to delete user', true);
    }
  };

  const showNotification = (message, isError = false) => {
    setNotification(message);
    setNotificationError(isError);
    setTimeout(() => setNotification(''), 4000);
  };

  const getUserLevelLabel = (level) => {
    if (level >= 200) return 'Administrator';
    if (level >= 100) return 'Staff';
    return 'Guest';
  };

  const getUserLevelValue = (label) => {
    switch (label) {
      case 'Administrator': return 200;
      case 'Staff': return 100;
      case 'Guest': return 0;
      default: return 0;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    const d = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  if (loading && isAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="mt-2 text-gray-600">
            {isAdmin ? 'Manage your group and all user accounts (admin).' : 'Manage your group and invite others.'}
          </p>
        </div>

        {notification && (
          <div className={`mb-4 p-4 rounded-lg border ${notificationError ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
            <p>{notification}</p>
          </div>
        )}

        {/* Group section: invite and members (staff+) */}
        {isStaff && (
          <div className="bg-white shadow-md rounded-xl border border-gray-200 overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#459EA3]/5 to-transparent">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#459EA3]" />
                Your group
              </h2>
              <p className="text-sm text-gray-600 mt-0.5">
                Invite people by email. Everyone in the group can see scripts, agents, logs, and analysis.
              </p>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invite by email</label>
                <form onSubmit={handleInvite} className="flex flex-wrap gap-2">
                  <input
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3] focus:border-[#459EA3]"
                  />
                  <button
                    type="submit"
                    disabled={inviteLoading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#459EA3] hover:bg-[#3d8a8e] text-white text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    <UserPlus className="w-4 h-4" />
                    {inviteLoading ? 'Sending…' : 'Send invite'}
                  </button>
                </form>
              </div>

              {groupLoading ? (
                <p className="text-sm text-gray-500">Loading group…</p>
              ) : (
                <>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                      <Users className="w-4 h-4" />
                      Users in your group ({groupMembers.length})
                    </h3>
                    <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {groupMembers.length === 0 ? (
                        <li className="px-4 py-3 text-sm text-gray-500">No one else in your group yet. Invite someone above.</li>
                      ) : (
                        groupMembers.map((m) => (
                          <li key={m.userId} className="px-4 py-3 flex items-center justify-between">
                            <div>
                              <span className="font-medium text-gray-900">{m.name || '—'}</span>
                              <span className="text-gray-500 text-sm ml-2">{m.email}</span>
                            </div>
                            <span className="text-xs text-gray-400">{formatDate(m.joinedAt)}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                      <Mail className="w-4 h-4" />
                      Pending invitations ({groupInvitations.length})
                    </h3>
                    <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {groupInvitations.length === 0 ? (
                        <li className="px-4 py-3 text-sm text-gray-500">No pending invitations.</li>
                      ) : (
                        groupInvitations.map((inv) => (
                          <li key={inv.id} className="px-4 py-3 flex items-center justify-between">
                            <span className="text-gray-700">{inv.email}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400">Expires {formatDate(inv.expiresAt)}</span>
                              <button
                                type="button"
                                onClick={() => handleCancelInvitation(inv.id)}
                                className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                                title="Cancel invitation"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* All users table — only visible to administrators */}
        {isAdmin && (
          <>
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                <h2 className="text-base font-bold text-gray-900">All users</h2>
                <p className="text-xs text-gray-500 mt-0.5">Only administrators can see and manage this list.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email Verified</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Level</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="email"
                            value={user.email}
                            onChange={(e) => handleUpdateUser(user.id, 'email', e.target.value)}
                            className="text-sm text-gray-900 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.created)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.lastLogin)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={user.verified === 1}
                            onChange={(e) => handleUpdateUser(user.id, 'verified', e.target.checked ? 1 : 0)}
                            className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={getUserLevelLabel(user.userlevel)}
                            onChange={(e) => handleUpdateUser(user.id, 'userlevel', getUserLevelValue(e.target.value))}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          >
                            <option>Guest</option>
                            <option>Staff</option>
                            <option>Administrator</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            className="text-red-400 hover:text-red-600 transition-colors bg-red-50 p-2 rounded-full hover:bg-red-100"
                            title="Delete User"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {users.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-500">No users found</p>
              </div>
            )}
          </>
        )}

        {!isStaff && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
            You need staff or administrator access to manage groups and users.
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
